// app/src/lib/cashflow/unallocated.js — "which bank outflows has nothing
// accounted for yet?"
//
// Most money leaving the checking account is already explained by some pipeline:
// payroll checks, tax payments, 401(k) contributions, owner draws, credit-card
// bill payments, revenue clawbacks, or an explicit Spending-tab rule. What's
// left is spend nobody has filed — the plumber's check, the one-off repair, the
// supplier paid by check that never got scanned. Those are invisible in the cost
// reports until someone picks a category for them.
//
// This module owns that single definition so the Today tab's Recent-activity
// tile and the Spending tab's unallocated list can never disagree about what
// "needs a category" means.
//
// Sign convention is Plaid's: amountCents > 0 is money OUT.

import { db } from '../db.js';
import * as accounts from './accounts.js';
import * as spendCategory from './spendCategory.js';
import * as payroll from './payroll.js';
import * as taxes from './taxes.js';
import * as ownerDraw from './ownerDraw.js';
import { bestVendorMatch } from '../procurement/vendorMatch.js';

/**
 * Everything the classifier needs, loaded once. Each lookup is independently
 * best-effort: a failure to read (say) tax keywords must not silently mark
 * every tax payment as unallocated, so a missing piece just disables ITS rule.
 * @returns {Promise<object>} opaque context for {@link classifyBankTx}
 */
export async function loadContext(userId) {
  const [spendCtx, guessKw, taxKw, checkRanges, ownerKw, k401Kw] = await Promise.all([
    spendCategory.loadExplicitContext(userId).catch(() => null),
    spendCategory.loadGuessKeywords().catch(() => null),
    taxes.getKeywords().catch(() => null),
    payroll.getCheckRanges().catch(() => []),
    ownerDraw.getKeywords().catch(() => []),
    ownerDraw.get401kKeywords().catch(() => []),
  ]);
  return { spendCtx, guessKw, taxKw, checkRanges, ownerKw, k401Kw };
}

/**
 * How settled is this transaction's classification?
 *
 * An explicit Spending rule / reconciled COGS, or a depository-pipeline match
 * (payroll checks, taxes, 401(k), owner draw, revenue clawback, card-bill
 * transfer) is a DECISION — settled, no picker. A keyword guess (rent,
 * utilities, maintenance, insurance) is only a DEFAULT: the row still counts as
 * unsettled so the operator confirms or reroutes it, but the guess rides along
 * to prefill the picker. Inflows are deposits, never categorized spend.
 *
 * @param {object} tx     plaidTransaction row
 * @param {boolean} isCard true when the tx belongs to a credit account
 * @param {object} ctx    from {@link loadContext}
 * @returns {{ settled: boolean, guess?: string }}
 */
export function classifyBankTx(tx, isCard, ctx) {
  const c = ctx || {};
  if ((tx?.amountCents || 0) <= 0) return { settled: true };       // money in
  if (accounts.isCardPayment?.(tx)) return { settled: true };      // card bill = transfer
  if (c.spendCtx && spendCategory.explicitCategory(tx, c.spendCtx)) return { settled: true };
  if (!isCard) {
    if (payroll.isPayrollCheck(tx, c.checkRanges)) return { settled: true };
    if (c.taxKw && taxes.classifyTax(tx, c.taxKw)) return { settled: true };
    if (payroll.is401kContribution?.(tx, c.k401Kw)) return { settled: true };
    if (ownerDraw.isOwnerDraw(tx, c.ownerKw)) return { settled: true };
  }
  return { settled: false, guess: (c.guessKw && spendCategory.guessCategory(tx, c.guessKw)) || '' };
}

/**
 * Unallocated CHECKING outflows in a date range, newest first.
 *
 * Deliberately depository-only: card charges have their own list on the
 * Spending tab, and mixing them here would double-count the same spend against
 * the card-bill payment that settles it.
 *
 * Pending rows are skipped — they can re-post under a new id when they clear,
 * which would orphan any per-transaction rule written against the pending id.
 *
 * @param {string} userId
 * @param {{ from:string, to:string }} range inclusive YYYY-MM-DD bounds
 * @returns {Promise<Array<{ plaidTxId, date, amountCents, merchantName, merchant, acctName, acctMask, guessCat }>>}
 */
export async function listUnallocated(userId, { from, to } = {}) {
  if (!userId || !from || !to) return [];
  const [accts, ctx, vendors] = await Promise.all([
    db.plaidAccounts.where('userId').equals(userId).toArray(),
    loadContext(userId),
    db.vendors.where('userId').equals(userId).toArray().catch(() => []),
  ]);
  const depository = new Map();
  for (const a of accts) if (a.type !== 'credit') depository.set(a.plaidAccountId, a);
  if (!depository.size) return [];

  // Inclusive on BOTH ends — Dexie's between() is exclusive by default and the
  // end-day rows would vanish (see reference-dexie-between-exclusive).
  const txs = await db.plaidTransactions
    .where('[userId+date]').between([userId, from], [userId, to], true, true).toArray();

  // Names are returned RAW: the UI runs them through cleanTxnName/demo.obscure
  // at render, the same chokepoint every other screen uses, so one merchant
  // reads identically everywhere.
  const out = [];
  for (const tx of txs) {
    if (tx.pending) continue;
    const acct = depository.get(tx.plaidAccountId);
    if (!acct) continue;
    const cls = classifyBankTx(tx, false, ctx);
    if (cls.settled) continue;
    // Does this payment look like it's settling a SUPPLIER invoice? Cheque and
    // ACH payments to suppliers aren't matched to scanned invoices yet, so they
    // land here looking like untagged spend — but the invoice behind them is
    // already in COGS. Filing this as COGS too would count the same food twice.
    // We can't decide it for the operator, so we name the supplier and let them.
    const supplier = bestVendorMatch(tx.merchantName || tx.name || '', vendors);
    out.push({
      plaidTxId: tx.plaidTxId,
      date: tx.date,
      amountCents: tx.amountCents || 0,
      merchantName: tx.merchantName || '',
      rawName: tx.merchantName || tx.name || '',
      acctName: acct.name || acct.subtype || 'Account',
      acctMask: acct.mask || '',
      guessCat: cls.guess || '',
      supplierName: supplier ? supplier.name : '',
    });
  }
  return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}
